import React, { useState } from 'react';
import { Box, Stack, Flex, Text, xcss } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Button, { LoadingButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { Field, CheckboxField } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { Checkbox } from '@atlaskit/checkbox';
import { Currency } from '@css-xp/graphtypes/src/generated-types-new';
import { Divider } from '@css-xp/base';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { Estimate } from './estimate';

interface Props {
  loading: boolean;
  currency: `${Currency}`;
  isSubmitting: boolean;
  isOrderConfirmationDisabled: boolean;
  breakup: {
    totalAmount: number;
    totalDiscount: number;
    partnerDiscount: number;
    taxPayable: number;
    adminFee: number;
  };
  isTransferOrder?: boolean;
  onAgreeToTnC: (state: boolean) => void;
  setSummaryDrawerOpen: (state: boolean) => void;
  hasValidationErrors?: boolean;
  renderValidationErrorPanel?: () => React.ReactNode;
}
const anchorStyles = xcss({
  textDecoration: 'underline',
  color: 'color.text.subtle',
  cursor: 'pointer',
});

const privacyLink = `https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers`;
const customerAgreementUrl = 'https://www.atlassian.com/legal/atlassian-customer-agreement#intro';

export const OrderSummaryTotalEstimate = ({
  loading,
  currency,
  isSubmitting,
  isOrderConfirmationDisabled,
  breakup,
  isTransferOrder,
  onAgreeToTnC,
  setSummaryDrawerOpen,
  hasValidationErrors,
  renderValidationErrorPanel,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCloudTnCAgreed, setCloudTnCAgreed] = useState(false);
  const [isPrivacyPolicyAgreed, setPrivacyPolicyAgreed] = useState(false);
  const [isTransferPolicyAgreed, setTransferPolicyAgreed] = useState(!isTransferOrder);
  // once all the checkboxes are checked, send the true state to order summary
  React.useEffect(() => {
    onAgreeToTnC(isPrivacyPolicyAgreed && isTransferPolicyAgreed && isCloudTnCAgreed);
  }, [isCloudTnCAgreed, isPrivacyPolicyAgreed, isTransferPolicyAgreed]);
  return (
    <Box paddingBlock="space.200" paddingInline="space.200">
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="medium">
          <div className="total">Total due now</div>
        </Heading>
        <div>
          <Estimate
            currency={currency}
            price={0}
            isTotal={true}
            loading={loading}
            style={{ fontSize: '20px', fontWeight: 500 }}
          />
        </div>
      </Flex>
      <Box paddingBlock="space.100">
        <Text size="small" color="color.text.subtle" as="p">
          Charged on next bill date and after any trials periods. Actual amount charged and invoiced may be different on
          user numbers added by customer.
        </Text>
      </Box>
      <Box paddingBlock="space.100">
        <Stack space="space.050">
          <Heading size="xsmall">Monthly estimates</Heading>
          <Flex justifyContent="space-between" alignItems="center">
            <div>Subtotal</div>
            <div>
              <Estimate currency={currency} price={breakup.totalAmount} loading={loading} />
            </div>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center">
            <div>Total discounts</div>
            <div>
              <Estimate currency={currency} price={-(breakup.totalDiscount ?? 0)} loading={loading} />
            </div>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center">
            <div>Partner discounts</div>
            <div>
              <Estimate currency={currency} price={breakup.partnerDiscount} loading={loading} />
            </div>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center">
            <div>Tax</div>
            <div>
              <Estimate currency={currency} price={breakup.taxPayable} loading={loading} />
            </div>
          </Flex>
          {breakup.adminFee ? (
            <Flex justifyContent="space-between" alignItems="center">
              <div>Total administration fee</div>
              <div>
                <Estimate currency={currency} price={breakup.adminFee} loading={loading} />
              </div>
            </Flex>
          ) : null}
        </Stack>
      </Box>
      <Box paddingBlock="space.100">
        <Field label="PO number" name="poNumber">
          {({ fieldProps }: any) => (
            <>
              <Textfield placeholder="" {...fieldProps} />
            </>
          )}
        </Field>
      </Box>
      <Divider />
      <Box paddingBlock="space.100">
        <CheckboxField name="ppc-order-review-tnc">
          {({ fieldProps }) => (
            <Checkbox
              {...fieldProps}
              value="tncAgree"
              isChecked={isCloudTnCAgreed}
              label={
                <Text color="color.text.subtle" size="small">
                  You agree to the Atlassian Cloud Terms of Service and Privacy Policy.
                </Text>
              }
              onChange={() => {
                setCloudTnCAgreed((prevState) => !prevState);
              }}
              testId="cloud-policy-tnc"
            />
          )}
        </CheckboxField>
        <CheckboxField name="ppc-order-privacy-policy">
          {({ fieldProps }) => (
            <Checkbox
              {...fieldProps}
              value="orderNotice"
              isChecked={isPrivacyPolicyAgreed}
              label={
                <Text color="color.text.subtle" size="small">
                  You agree to the{' '}
                  <Anchor
                    xcss={anchorStyles}
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setIsOpen(true);
                    }}
                  >
                    Order notice
                  </Anchor>{' '}
                  and{' '}
                  <Anchor
                    xcss={anchorStyles}
                    testId="atlassian-privacy-link"
                    href={customerAgreementUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Program Term for Partner Purchasing Center
                  </Anchor>
                  , and acknowledge the{' '}
                  <Anchor
                    xcss={anchorStyles}
                    testId="atlassian-privacy-link"
                    href={privacyLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy.
                  </Anchor>
                </Text>
              }
              onChange={() => {
                setPrivacyPolicyAgreed((prevState) => !prevState);
              }}
              testId="privacy-policy-tnc"
            />
          )}
        </CheckboxField>
        <ModalTransition>
          {isOpen && (
            <Modal onClose={() => setIsOpen(false)}>
              <ModalHeader>
                <ModalTitle>Order Notice</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Stack space="space.100">
                  <Text>
                    You acknowledge that your order contains monthly auto-renewal subscriptions. You agree to pay the
                    total amount each billing cycle from the next bill date and after any trial periods. Amount may
                    change if your customer adjust that quantity of users. A prorated amount may first be charged to
                    align your bill date with other subscriptions.
                  </Text>
                  <Text>
                    Your customer can cancel any time from admin.atlassian.com/partner-managed-subscriptions. If our
                    prices change, we’ll notify you.
                  </Text>
                </Stack>
              </ModalBody>
              <ModalFooter>
                <div id="warning-confirm-footer">
                  <Button appearance="primary" onClick={() => setIsOpen(false)} autoFocus>
                    Confirm
                  </Button>
                </div>
              </ModalFooter>
            </Modal>
          )}
        </ModalTransition>
        {isTransferOrder ? (
          <CheckboxField name="ppc-order-review-tnc">
            {({ fieldProps }) => (
              <Checkbox
                {...fieldProps}
                value="transfer-consent"
                isChecked={isTransferPolicyAgreed}
                label={
                  <Text color="color.text.subtle" size="small">
                    The customer has consented to transferring the subscriptions to your billing account.
                  </Text>
                }
                onChange={() => {
                  setTransferPolicyAgreed((prevState) => !prevState);
                }}
                testId={'tnc-checkbox'}
              />
            )}
          </CheckboxField>
        ) : null}
      </Box>
      <Box paddingBlock="space.100">
        <Stack grow="fill" space="space.100">
          {hasValidationErrors && renderValidationErrorPanel && renderValidationErrorPanel()}
          <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.ORDER}>
            <LoadingButton
              type="submit"
              appearance="primary"
              testId={'ppc-order-review.place-order'}
              isLoading={isSubmitting}
              isDisabled={isOrderConfirmationDisabled}
            >
              Confirm order
            </LoadingButton>
          </AdvocateAccessWall>
          <Button appearance="subtle" onClick={() => setSummaryDrawerOpen(false)}>
            Back
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
